import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportError } from "../lib/report-error.js";

/**
 * Catches render-time exceptions in its subtree so one throwing component shows
 * a readable message instead of a blank white screen (React unmounts the whole
 * tree on an uncaught render error). Without this, any throw in a route renders
 * nothing at all, which is impossible to diagnose from the outside.
 *
 * Shows the error message + a short stack inline. Keep it lightweight and
 * dependency-free so it can wrap anything.
 */
type Props = {
  children: ReactNode;
  label?: string;
  /**
   * When this value changes, a caught error is cleared. Pass the route path so a
   * crash on one screen doesn't brick the whole app — navigating away recovers
   * instead of the fallback sticking until a full reload.
   */
  resetKey?: string | number;
};
type State = { error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surfaced in the browser console for remote debugging.
    console.error("[ErrorBoundary]", this.props.label ?? "", error, info.componentStack);
    // Report so the crash is visible in /app/admin, not just the user's console.
    reportError(this.props.label, error, info.componentStack ?? undefined);
  }

  componentDidUpdate(prev: Props) {
    // Recover on navigation: when the resetKey (route path) changes while an
    // error is showing, clear it so the next screen renders normally.
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      const e = this.state.error;
      return (
        <div
          style={{
            minHeight: "60vh",
            display: "flex",
            flexDirection: "column",
            gap: 12,
            alignItems: "flex-start",
            justifyContent: "center",
            maxWidth: 560,
            margin: "0 auto",
            padding: "40px 24px",
            fontFamily: "var(--font-sans)",
          }}
        >
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "var(--signal-red)",
            }}
          >
            Something broke on this screen
          </div>
          <p style={{ color: "var(--fg-1)", fontSize: 14, lineHeight: 1.55, margin: 0 }}>
            {e.message || String(e)}
          </p>
          {e.stack && (
            <pre
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                color: "var(--fg-3)",
                background: "var(--bg-1)",
                border: "1px solid var(--border-line)",
                borderRadius: 6,
                padding: 10,
                maxWidth: "100%",
                overflowX: "auto",
                whiteSpace: "pre-wrap",
              }}
            >
              {e.stack.split("\n").slice(0, 8).join("\n")}
            </pre>
          )}
          {/* Reload first, and deliberately. A large share of what lands here is
              a stale tab whose chunk hashes a deploy replaced, and for that the
              fix is literally a reload — but the only affordance used to be
              "Back home", which navigates within the SAME stale shell and so
              cannot fix it. A student on a finished practice test was sent home
              from their own score report nine times. */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 12,
                color: "var(--bg-0)",
                background: "var(--accent-amber)",
                border: "1px solid var(--accent-amber)",
                borderRadius: 4,
                padding: "8px 16px",
                minHeight: 44,
                cursor: "pointer",
              }}
            >
              Reload this page
            </button>
            <a
              href="/app/home"
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 12,
                color: "var(--accent-amber)",
                textDecoration: "none",
                border: "1px solid var(--accent-amber)",
                borderRadius: 4,
                padding: "8px 16px",
                minHeight: 44,
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              Back home
            </a>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
